import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { pctSymbol, currencySymbol } from '../../utils/formatters';

// Lightweight shadcn-like card wrappers using CSS variables
const Card = ({ className = '', children }) => (
  <div className={`rounded-md border border-succession-border bg-succession-bg-primary ${className}`}>{children}</div>
);
const CardHeader = ({ children }) => (
  <div className="px-4 pt-4">{children}</div>
);
const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-xl font-semibold text-succession-text-primary ${className}`}>{children}</h3>
);
const CardContent = ({ children }) => (
  <div className="p-4">{children}</div>
);

// Resolve a CSS variable to a concrete color string from the active tenant theme
const resolveVar = (varName, fallback) => {
  try {
    if (typeof window === 'undefined' || !window.document) return fallback || `var(${varName})`;
    const val = getComputedStyle(document.documentElement).getPropertyValue(varName);
    return (val && val.trim()) ? val.trim() : (fallback || `var(${varName})`);
  } catch (e) {
    return fallback || `var(${varName})`;
  }
};

// Line display names
const LINE_LABELS = {
  wealth: 'Wealth',
  life: 'Life',
  pc: 'P&C',
  group: 'Group',
};

// Get line color using CSS variables with semantic fallbacks
// Uses primary color for first line, secondary for others to create visual distinction
const getLineColor = (lineId, index) => {
  // Use succession theme CSS variables
  const primaryColor = resolveVar('--succession-primary', '#5489B6');
  const secondaryColor = resolveVar('--succession-secondary', '#002949');
  const accentColor = resolveVar('--succession-accent', '#5B5E5A');
  
  // Assign colors based on line index for visual distinction
  const colorMap = {
    0: primaryColor,   // First line uses primary
    1: secondaryColor, // Second line uses secondary
    2: accentColor,    // Third line uses accent
    3: resolveVar('--succession-neutral', '#666666'), // Fourth line uses neutral
  };
  
  return colorMap[index] || primaryColor;
};

const formatCurrency = (value) => {
  const v = Number(value || 0);
  const sym = currencySymbol();
  if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${sym}${Math.round(v / 1_000)}K`;
  return `${sym}${v.toFixed(0)}`;
};

const formatCurrencyFull = (value) => {
  const v = Number(value || 0);
  return `${currencySymbol()}${v.toLocaleString()}`;
};

// Custom tooltip component using CSS variables
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div 
      className="rounded-md p-3 shadow-lg"
      style={{
        backgroundColor: 'var(--succession-bg-primary, #ffffff)',
        border: '1px solid var(--succession-border, #e5e7eb)',
      }}
    >
      <p 
        className="font-semibold mb-1"
        style={{ color: 'var(--succession-text-primary, #111827)' }}
      >
        {label}
      </p>
      {payload.map((entry, index) => (
        <div 
          key={index} 
          className="text-sm"
          style={{ color: 'var(--succession-text-primary, #111827)' }}
        >
          {formatCurrencyFull(entry.value)}
        </div>
      ))}
    </div>
  );
};

/**
 * Calculate average growth rate from historical values
 * Uses the same formula as the DCF tab in MultiLineValuation.jsx
 * @param {Object} history - Object with y1, y2, y3, y4, y5 values (y1 = most recent)
 * @returns {number} Average growth rate as a percentage (e.g., 6.06)
 */
const calculateGrowthRate = (history) => {
  if (!history) return 0;
  
  const vals = [
    Number(history.y1 || 0),
    Number(history.y2 || 0),
    Number(history.y3 || 0),
    Number(history.y4 || 0),
    Number(history.y5 || 0),
  ].filter((v) => Number.isFinite(v) && v > 0);
  
  if (vals.length < 2) return 0;
  
  const growths = [];
  for (let i = 0; i < vals.length - 1; i++) {
    const current = vals[i];
    const next = vals[i + 1];
    if (current > 0 && Number.isFinite(next)) {
      growths.push((current - next) / current);
    }
  }
  
  if (!growths.length) return 0;
  
  // Divide by vals.length (# of data points) to match backend rj_gross_revenue_detail.rb
  const avg = growths.reduce((s, x) => s + x, 0) / vals.length;
  return Math.round((avg * 100) * 100) / 100; // Round to 2 decimal places
};

/**
 * Check if a line has valid historical data (at least 2 years)
 * @param {Object} history - Object with y1, y2, y3, y4, y5 values
 * @returns {boolean}
 */
const hasValidHistory = (history) => {
  if (!history) return false;
  const vals = [
    Number(history.y1 || 0),
    Number(history.y2 || 0),
    Number(history.y3 || 0),
    Number(history.y4 || 0),
    Number(history.y5 || 0),
  ].filter((v) => Number.isFinite(v) && v > 0);
  return vals.length >= 2;
};

/**
 * Single line historical revenue chart
 */
const SingleLineChart = ({ lineId, history, currentYear, lineIndex }) => {
  const intl = useIntl();
  // Get bar color from CSS variables based on line index
  const barColor = getLineColor(lineId, lineIndex);
  const growthRate = calculateGrowthRate(history);
  
  // Get theme colors from CSS variables
  const borderColor = resolveVar('--succession-border', '#e5e7eb');
  const textPrimaryColor = resolveVar('--succession-text-primary', '#111827');
  const textSecondaryColor = resolveVar('--succession-text-secondary', '#6b7280');
  
  // Build chart data - oldest to newest (left to right)
  const chartData = useMemo(() => {
    const data = [];
    const years = [
      { key: 'y5', year: currentYear - 5 },
      { key: 'y4', year: currentYear - 4 },
      { key: 'y3', year: currentYear - 3 },
      { key: 'y2', year: currentYear - 2 },
      { key: 'y1', year: currentYear - 1 },
    ];
    
    years.forEach(({ key, year }) => {
      const value = Number(history?.[key] || 0);
      if (value > 0) {
        data.push({
          year: year.toString(),
          revenue: value,
        });
      }
    });
    
    return data;
  }, [history, currentYear]);
  
  if (chartData.length < 2) return null;
  
  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <CardTitle>{LINE_LABELS[lineId]} {intl.formatMessage({ id: 'succession.multi_line_valuation.historical_gross_revenue' })}</CardTitle>
          <div 
            className="px-4 py-2 rounded"
            style={{
              border: `2px solid ${textPrimaryColor}`,
            }}
          >
            <span 
              className="text-sm"
              style={{ color: textSecondaryColor }}
            >
              {intl.formatMessage({ id: 'succession.multi_line_valuation.average_growth_rate' })}
            </span>
            <span
              className="text-lg font-bold ml-1"
              style={{ color: textPrimaryColor }}
            >
              {growthRate.toFixed(2)}{pctSymbol()}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--succession-border)" vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fill: 'var(--succession-text-secondary)', fontSize: 14 }}
              axisLine={{ stroke: 'var(--succession-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--succession-text-secondary)', fontSize: 12 }}
              tickFormatter={formatCurrency}
              axisLine={{ stroke: 'var(--succession-border)' }}
              tickLine={false}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="revenue"
              fill={barColor}
              radius={[0, 0, 0, 0]}
              maxBarSize={80}
            >
              <LabelList
                dataKey="revenue"
                position="top"
                formatter={formatCurrency}
                fill="var(--succession-text-secondary)"
                fontSize={11}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

/**
 * HistoricalRevenueChart - Displays historical gross revenue charts for each line
 * 
 * @param {Object} props
 * @param {Object} props.lineInputs - Input data for each line (wealth, life, pc, group)
 * @param {Array} props.enabledLines - Array of enabled line IDs
 * @param {string} props.className - Additional CSS classes
 */
export default function HistoricalRevenueChart({
  lineInputs = {},
  enabledLines = ['wealth'],
  className = ''
}) {
  const currentYear = typeof window !== 'undefined' ? new Date().getFullYear() : new Date().getFullYear();
  
  // Filter to lines that have valid historical data
  const linesWithHistory = useMemo(() => {
    return enabledLines.filter((lineId) => {
      const inputs = lineInputs[lineId] || {};
      return hasValidHistory(inputs.history);
    });
  }, [lineInputs, enabledLines]);
  
  if (linesWithHistory.length === 0) {
    return null;
  }
  
  return (
    <div className={className}>
      {linesWithHistory.map((lineId, index) => (
        <SingleLineChart
          key={lineId}
          lineId={lineId}
          lineIndex={index}
          history={lineInputs[lineId]?.history}
          currentYear={currentYear}
        />
      ))}
    </div>
  );
}
